import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import PushToggle from '../components/PushToggle';
import {
  Users, FileText, CheckCircle, XCircle, Clock, AlertCircle, TrendingUp, Settings,
  FileEdit, Trash2, Plus, Inbox, Download, BarChart3, Phone, Mail, MessageSquare, Megaphone, Receipt, DollarSign, CreditCard, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line
} from 'recharts';
import MarketingTab from '../components/MarketingTab';
import ConsultantProductivity from '../components/ConsultantProductivity';

const STATUS_COLORS = {
  pending: '#F59E0B',
  under_review: '#3B82F6',
  approved: '#10B981',
  rejected: '#EF4444',
  new: '#F59E0B',
  contacted: '#3B82F6',
  qualified: '#8B5CF6',
  converted: '#10B981',
  closed: '#64748B',
};

const downloadFile = async (url, filename) => {
  const token = localStorage.getItem('arthik_token');
  try {
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast.success(`Downloaded ${filename}`);
  } catch (e) {
    toast.error('Download failed');
  }
};

const TABS = [
  { key: 'overview', label: 'Overview', icon: TrendingUp },
  { key: 'applications', label: 'Applications', icon: FileText },
  { key: 'enquiries', label: 'Enquiries', icon: Inbox },
  { key: 'tax', label: 'Tax Cases', icon: Receipt },
  { key: 'consultant-approvals', label: 'CA Approvals', icon: UserCheck },
  { key: 'messages', label: 'Messages', icon: MessageSquare },
  { key: 'marketing', label: 'Marketing', icon: Megaphone },
  { key: 'ads', label: 'Ad Bookings', icon: DollarSign },
  { key: 'payments', label: 'Payments', icon: CreditCard },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'content', label: 'About Content', icon: FileEdit },
];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const queryTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(queryTab && TABS.find(t => t.key === queryTab) ? queryTab : 'overview');
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [enquiryStatusCounts, setEnquiryStatusCounts] = useState({});
  const [enquiriesByCity, setEnquiriesByCity] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [contactTotal, setContactTotal] = useState(0);
  const [taxCases, setTaxCases] = useState([]);
  const [taxByStatus, setTaxByStatus] = useState({});
  const [taxFilter, setTaxFilter] = useState('');
  const [editingTax, setEditingTax] = useState(null);
  const [taxForm, setTaxForm] = useState({ status: '', admin_notes: '', quoted_amount: '', assigned_to: '' });
  const [consultants, setConsultants] = useState([]);
  const [newConsultant, setNewConsultant] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [consultantApps, setConsultantApps] = useState([]);
  const [consultantAppsByStatus, setConsultantAppsByStatus] = useState({});
  const [consultantAppFilter, setConsultantAppFilter] = useState('kyc_submitted');
  const [viewingApp, setViewingApp] = useState(null);
  const [ads, setAds] = useState([]);
  const [adsByStatus, setAdsByStatus] = useState({});
  const [adsRevenue, setAdsRevenue] = useState(0);
  const [adFilter, setAdFilter] = useState('');
  const [payments, setPayments] = useState([]);
  const [paymentsTotalInr, setPaymentsTotalInr] = useState(0);
  const [paymentFilter, setPaymentFilter] = useState('');
  const [aboutContent, setAboutContent] = useState(null);
  const [savingAbout, setSavingAbout] = useState(false);
  const [filter, setFilter] = useState('');
  const [enqFilter, setEnqFilter] = useState('');
  const [enqCityFilter, setEnqCityFilter] = useState('');
  const [editingApp, setEditingApp] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', notes: '' });
  const [editingEnq, setEditingEnq] = useState(null);
  const [enqForm, setEnqForm] = useState({ status: '', admin_notes: '' });

  // Sync tab with URL
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t && TABS.find(x => x.key === t) && t !== activeTab) setActiveTab(t);
  }, [location.search]);

  const setTab = (key) => {
    setActiveTab(key);
    navigate(`/admin?tab=${key}`, { replace: true });
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [activeTab, filter, enqFilter, enqCityFilter, taxFilter, adFilter, paymentFilter, consultantAppFilter]);

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };
    try {
      if (activeTab === 'overview') {
        const [s, t] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/admin/stats/trends?days=14`, { headers }),
        ]);
        setStats(s.data);
        setTrends(t.data);
      } else if (activeTab === 'applications') {
        const url = filter ? `${API}/admin/applications?status=${filter}` : `${API}/admin/applications`;
        const res = await axios.get(url, { headers });
        setApplications(res.data.applications);
      } else if (activeTab === 'enquiries') {
        const params = new URLSearchParams();
        if (enqFilter) params.set('status', enqFilter);
        if (enqCityFilter) params.set('city', enqCityFilter);
        const url = `${API}/admin/enquiries${params.toString() ? '?' + params.toString() : ''}`;
        const res = await axios.get(url, { headers });
        setEnquiries(res.data.enquiries);
        setEnquiryStatusCounts(res.data.by_status || {});
        setEnquiriesByCity(res.data.by_city || []);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API}/admin/users`, { headers });
        setUsers(res.data.users);
      } else if (activeTab === 'messages') {
        const res = await axios.get(`${API}/admin/contact-messages`, { headers });
        setContactMessages(res.data.messages || []);
        setContactTotal(res.data.total || 0);
      } else if (activeTab === 'ads') {
        const url = adFilter ? `${API}/admin/advertisements?status=${adFilter}` : `${API}/admin/advertisements`;
        const res = await axios.get(url, { headers });
        setAds(res.data.ads || []);
        setAdsByStatus(res.data.by_status || {});
        setAdsRevenue(res.data.total_revenue_won || 0);
      } else if (activeTab === 'payments') {
        const url = paymentFilter ? `${API}/admin/payments?status=${paymentFilter}` : `${API}/admin/payments`;
        const res = await axios.get(url, { headers });
        setPayments(res.data.payments || []);
        setPaymentsTotalInr(res.data.total_paid_inr || 0);
      } else if (activeTab === 'tax') {
        const [casesRes, conRes] = await Promise.all([
          axios.get(taxFilter ? `${API}/admin/tax-cases?status=${taxFilter}` : `${API}/admin/tax-cases`, { headers }),
          axios.get(`${API}/admin/consultants`, { headers }).catch(() => ({ data: { consultants: [] } })),
        ]);
        setTaxCases(casesRes.data.cases || []);
        setTaxByStatus(casesRes.data.by_status || {});
        setConsultants(conRes.data.consultants || []);
      } else if (activeTab === 'consultant-approvals') {
        const url = consultantAppFilter
          ? `${API}/admin/consultant-applications?status=${consultantAppFilter}`
          : `${API}/admin/consultant-applications`;
        const res = await axios.get(url, { headers });
        setConsultantApps(res.data.applications || []);
        setConsultantAppsByStatus(res.data.by_status || {});
      } else if (activeTab === 'content') {
        const res = await axios.get(`${API}/about`);
        setAboutContent(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch data');
    }
  };

  const handleSaveAbout = async () => {
    setSavingAbout(true);
    try {
      await axios.patch(`${API}/admin/about`, {
        mission: aboutContent.mission,
        vision: aboutContent.vision,
        team_members: aboutContent.team_members || [],
        awards: aboutContent.awards || [],
        press_coverage: aboutContent.press_coverage || [],
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` } });
      toast.success('About content updated!');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSavingAbout(false);
    }
  };

  const updateArrayField = (field, idx, key, val) => {
    const arr = [...(aboutContent[field] || [])];
    arr[idx] = { ...arr[idx], [key]: val };
    setAboutContent({ ...aboutContent, [field]: arr });
  };
  const addArrayItem = (field, template) => setAboutContent({ ...aboutContent, [field]: [...(aboutContent[field] || []), template] });
  const removeArrayItem = (field, idx) => {
    const arr = [...(aboutContent[field] || [])];
    arr.splice(idx, 1);
    setAboutContent({ ...aboutContent, [field]: arr });
  };

  const handleUpdateApplication = async () => {
    try {
      await axios.patch(`${API}/admin/applications/${editingApp.id}`, editForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Application updated! Email + push sent.');
      setEditingApp(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleUpdateEnquiry = async () => {
    try {
      await axios.patch(`${API}/admin/enquiries/${editingEnq.id}`, enqForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Enquiry updated');
      setEditingEnq(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Enquiry deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleUpdateTaxCase = async () => {
    try {
      const payload = {
        status: taxForm.status || null,
        admin_notes: taxForm.admin_notes || null,
        quoted_amount: taxForm.quoted_amount ? Number(taxForm.quoted_amount) : null,
        assigned_to: taxForm.assigned_to || null,
      };
      await axios.patch(`${API}/admin/tax-cases/${editingTax.id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Tax case updated');
      setEditingTax(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleApproveConsultantApp = async (appId) => {
    if (!window.confirm('Approve this consultant? They will be sent login credentials by email.')) return;
    try {
      const res = await axios.post(`${API}/admin/consultant-applications/${appId}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success(`Approved! Alias: ${res.data.alias}`);
      setViewingApp(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const handleRejectConsultantApp = async (appId) => {
    const reason = window.prompt('Reason for rejection (sent to candidate by email):', 'Did not meet eligibility criteria');
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/consultant-applications/${appId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Application rejected; email sent.');
      setViewingApp(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    }
  };

  const handleCreateConsultant = async () => {
    if (!newConsultant.full_name || !newConsultant.email || !newConsultant.phone || !newConsultant.password) {
      toast.error('Fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/consultants`, newConsultant, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Consultant created');
      setNewConsultant({ full_name: '', email: '', phone: '', password: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create consultant');
    }
  };

  const handleDeleteTaxCase = async (id) => {
    if (!window.confirm('Delete this tax case?')) return;
    try {
      await axios.delete(`${API}/admin/tax-cases/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Tax case deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleUpdateAd = async (id, payload) => {
    try {
      await axios.patch(`${API}/admin/advertisements/${id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Ad updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleDeleteAd = async (id) => {
    if (!window.confirm('Delete this ad booking?')) return;
    try {
      await axios.delete(`${API}/admin/advertisements/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` },
      });
      toast.success('Ad deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };


  const statusPieData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: 'Pending', value: stats.pending_applications, color: STATUS_COLORS.pending },
      { name: 'Under Review', value: stats.under_review_applications, color: STATUS_COLORS.under_review },
      { name: 'Approved', value: stats.approved_applications, color: STATUS_COLORS.approved },
      { name: 'Rejected', value: stats.rejected_applications, color: STATUS_COLORS.rejected },
    ].filter(d => d.value > 0);
  }, [stats]);

  const bankBarData = useMemo(() => {
    if (!stats?.applications_by_bank) return [];
    return Object.entries(stats.applications_by_bank).map(([name, count]) => ({ name, count })).slice(0, 8);
  }, [stats]);

  const serviceBarData = useMemo(() => {
    if (!stats?.applications_by_service) return [];
    return Object.entries(stats.applications_by_service).map(([name, count]) => ({ name, count })).slice(0, 8);
  }, [stats]);

  if (user?.role !== 'admin') return null;

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(1.5rem, 4vw, 4rem) clamp(1rem, 3vw, 2rem)' }}>
        <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Settings size={28} style={{ color: '#FF6B35' }} />
              <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)', color: '#0F172A', margin: 0 }}>Admin Dashboard</h1>
            </div>
            <p style={{ fontSize: 'clamp(0.9rem, 2vw, 1.125rem)', color: '#475569', margin: 0 }}>Applications, enquiries, analytics & content</p>
          </div>
          <PushToggle />
        </div>

        {/* Tabs - desktop */}
        <div className="admin-tab-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setTab(tab.key)}
                className={isActive ? 'primary-button' : 'outline-button'}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}
                data-testid={`admin-tab-${tab.key}`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && stats && (
          <div className="fade-in" data-testid="admin-overview-section">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <StatCard icon={Users} label="Total Users" value={stats.total_users} testId="admin-stat-users" />
              <StatCard icon={FileText} label="Applications" value={stats.total_applications} testId="admin-stat-total-apps" />
              <StatCard icon={Inbox} label="Enquiries" value={Object.values(enquiryStatusCounts).reduce((a,b)=>a+b,0) || '-'} hint="See Enquiries tab" />
              <StatCard icon={Clock} label="Pending" value={stats.pending_applications} color="#F59E0B" />
              <StatCard icon={AlertCircle} label="Under Review" value={stats.under_review_applications} color="#3B82F6" />
              <StatCard icon={CheckCircle} label="Approved" value={stats.approved_applications} color="#10B981" />
              <StatCard icon={XCircle} label="Rejected" value={stats.rejected_applications} color="#EF4444" />
              <StatCard icon={TrendingUp} label="Referral Payout" value={`₹${(stats.total_referral_earnings || 0).toLocaleString()}`} color="#FF6B35" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <ChartCard title="Application Status">
                {statusPieData.length === 0 ? (
                  <EmptyMsg>No applications yet</EmptyMsg>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={statusPieData} dataKey="value" nameKey="name" outerRadius={90} label>
                        {statusPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="14-Day Trend">
                {trends?.series?.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={trends.series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="applications" stroke="#FF6B35" strokeWidth={2} />
                      <Line type="monotone" dataKey="enquiries" stroke="#3B82F6" strokeWidth={2} />
                      <Line type="monotone" dataKey="approved" stroke="#10B981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyMsg>No data</EmptyMsg>}
              </ChartCard>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
              <ChartCard title="Applications by Bank">
                {bankBarData.length === 0 ? <EmptyMsg>No applications yet</EmptyMsg> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={bankBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FF6B35" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
              <ChartCard title="Applications by Service">
                {serviceBarData.length === 0 ? <EmptyMsg>No applications yet</EmptyMsg> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={serviceBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>
          </div>
        )}

        {/* Applications */}
        {activeTab === 'applications' && (
          <div className="fade-in" data-testid="admin-applications-section">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {['', 'pending', 'under_review', 'approved', 'rejected'].map((s) => (
                  <button
                    key={s || 'all'}
                    onClick={() => setFilter(s)}
                    className={filter === s ? 'primary-button' : 'outline-button'}
                    style={{ fontSize: '0.85rem', padding: '0.45rem 0.9rem', textTransform: 'capitalize' }}
                    data-testid={`admin-filter-${s || 'all'}`}
                  >
                    {s ? s.replace('_', ' ') : 'All'}
                  </button>
                ))}
              </div>
              <button
                className="outline-button"
                onClick={() => downloadFile(`${API}/admin/applications/export${filter ? `?status=${filter}` : ''}`, 'applications.csv')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}
                data-testid="export-applications-csv"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
            <ApplicationsTable applications={applications} onEdit={(app) => { setEditingApp(app); setEditForm({ status: app.status, notes: '' }); }} />
          </div>
        )}

        {/* Enquiries */}
        {activeTab === 'enquiries' && (
          <div className="fade-in" data-testid="admin-enquiries-section">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {['new', 'contacted', 'qualified', 'converted', 'closed'].map((s) => (
                <div key={s} className="stat-card" style={{ borderLeft: `4px solid ${STATUS_COLORS[s]}`, padding: '0.9rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#475569', textTransform: 'capitalize' }}>{s}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A' }}>{enquiryStatusCounts[s] || 0}</div>
                </div>
              ))}
            </div>

            {/* Top cities leaderboard — for location-based advertising decisions */}
            {enquiriesByCity.length > 0 && (
              <div style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FED7AA 100%)', borderRadius: 14, padding: '1.1rem 1.25rem', marginBottom: '1.5rem' }} data-testid="enquiries-by-city">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.65rem' }}>
                  <span style={{ fontSize: '1rem' }}>📍</span>
                  <strong style={{ color: '#9A3412', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Top cities by enquiries — target your ads here</strong>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {enquiriesByCity.slice(0, 15).map((c) => {
                    const isActive = enqCityFilter.toLowerCase() === c.city.toLowerCase();
                    return (
                      <button
                        key={c.city}
                        onClick={() => setEnqCityFilter(isActive ? '' : c.city)}
                        data-testid={`city-chip-${c.city.toLowerCase().replace(/[^a-z]/g,'-')}`}
                        title={`Click to filter enquiries from ${c.city}`}
                        style={{
                          padding: '6px 12px',
                          background: isActive ? '#FF6B35' : 'white',
                          color: isActive ? 'white' : '#9A3412',
                          border: '1px solid ' + (isActive ? '#FF6B35' : '#FED7AA'),
                          borderRadius: 999,
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        {c.city}
                        <span style={{ background: isActive ? 'rgba(255,255,255,0.25)' : '#FFE8D6', color: isActive ? 'white' : '#9A3412', padding: '1px 7px', borderRadius: 999, fontSize: '0.7rem' }}>{c.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {['', 'new', 'contacted', 'qualified', 'converted', 'closed'].map((s) => (
                  <button
                    key={s || 'all'}
                    onClick={() => setEnqFilter(s)}
                    className={enqFilter === s ? 'primary-button' : 'outline-button'}
                    style={{ fontSize: '0.85rem', padding: '0.45rem 0.9rem', textTransform: 'capitalize' }}
                    data-testid={`enquiry-filter-${s || 'all'}`}
                  >
                    {s ? s : 'All'}
                  </button>
                ))}
              </div>
              <button
                className="outline-button"
                onClick={() => downloadFile(`${API}/admin/enquiries/export${enqFilter ? `?status=${enqFilter}` : ''}`, 'enquiries.csv')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}
                data-testid="export-enquiries-csv"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
            <EnquiriesTable
              enquiries={enquiries}
              onEdit={(en) => { setEditingEnq(en); setEnqForm({ status: en.status, admin_notes: en.admin_notes || '' }); }}
              onDelete={handleDeleteEnquiry}
            />
          </div>
        )}

        {/* Tax Cases */}
        {activeTab === 'tax' && (
          <div className="fade-in" data-testid="admin-tax-section">
            {/* Productivity report */}
            <ConsultantProductivity />

            {/* Consultants management strip */}
            <div className="calculator-section" style={{ padding: '1rem', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ fontSize: '0.95rem', color: '#0F172A', fontWeight: 700 }}>
                  Tax Consultants ({consultants.length})
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {consultants.slice(0, 5).map((c) => (
                    <span key={c.id} style={{ background: '#ECFDF5', color: '#065F46', padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }} data-testid={`consultant-chip-${c.id}`}>
                      {c.full_name} ({c.open_cases || 0} open)
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem', marginTop: '0.75rem' }}>
                <input className="input-field" placeholder="Full name" value={newConsultant.full_name} onChange={(e) => setNewConsultant({ ...newConsultant, full_name: e.target.value })} data-testid="new-consultant-name" style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }} />
                <input className="input-field" placeholder="Email" type="email" value={newConsultant.email} onChange={(e) => setNewConsultant({ ...newConsultant, email: e.target.value })} data-testid="new-consultant-email" style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }} />
                <input className="input-field" placeholder="Phone" value={newConsultant.phone} onChange={(e) => setNewConsultant({ ...newConsultant, phone: e.target.value })} data-testid="new-consultant-phone" style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }} />
                <input className="input-field" placeholder="Initial password" type="password" value={newConsultant.password} onChange={(e) => setNewConsultant({ ...newConsultant, password: e.target.value })} data-testid="new-consultant-password" style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }} />
                <button onClick={handleCreateConsultant} className="primary-button" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', fontSize: '0.85rem' }} data-testid="create-consultant-button">+ Add Consultant</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {['', 'new', 'documents_pending', 'under_review', 'filed', 'completed', 'cancelled'].map((s) => (
                <button
                  key={s || 'all'}
                  onClick={() => setTaxFilter(s)}
                  data-testid={`tax-filter-${s || 'all'}`}
                  style={{
                    padding: '0.4rem 0.85rem',
                    background: taxFilter === s ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'white',
                    color: taxFilter === s ? 'white' : '#475569',
                    border: '1px solid ' + (taxFilter === s ? '#10B981' : '#E2E8F0'),
                    borderRadius: 999,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {(s || 'all').replace('_', ' ')}{s && taxByStatus[s] !== undefined ? ` (${taxByStatus[s]})` : ''}
                </button>
              ))}
            </div>
            <TaxCasesTable cases={taxCases} consultants={consultants} onEdit={(c) => { setEditingTax(c); setTaxForm({ status: c.status, admin_notes: c.admin_notes || '', quoted_amount: c.quoted_amount || '', assigned_to: c.assigned_to || '' }); }} onDelete={handleDeleteTaxCase} />
          </div>
        )}

        {/* Consultant Approvals (CA onboarding pipeline) */}
        {activeTab === 'consultant-approvals' && (
          <div className="fade-in" data-testid="admin-consultant-approvals-section">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
              {[
                { key: 'signed_up', label: 'Signed Up', color: '#94A3B8' },
                { key: 'test_passed', label: 'Passed Test', color: '#3B82F6' },
                { key: 'kyc_submitted', label: 'KYC Submitted', color: '#F59E0B' },
                { key: 'verified', label: 'Verified', color: '#10B981' },
                { key: 'rejected', label: 'Rejected', color: '#EF4444' },
              ].map((s) => (
                <div key={s.key} className="stat-card" style={{ borderLeft: `4px solid ${s.color}`, padding: '0.9rem' }} data-testid={`ca-stat-${s.key}`}>
                  <div style={{ fontSize: '0.75rem', color: '#475569', textTransform: 'capitalize' }}>{s.label}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A' }}>{consultantAppsByStatus[s.key] || 0}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {['kyc_submitted', 'test_passed', 'signed_up', 'verified', 'rejected', ''].map((s) => (
                <button
                  key={s || 'all'}
                  onClick={() => setConsultantAppFilter(s)}
                  data-testid={`ca-filter-${s || 'all'}`}
                  style={{
                    padding: '0.4rem 0.85rem',
                    background: consultantAppFilter === s ? 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)' : 'white',
                    color: consultantAppFilter === s ? 'white' : '#475569',
                    border: '1px solid ' + (consultantAppFilter === s ? '#FF6B35' : '#E2E8F0'),
                    borderRadius: 999, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize',
                  }}
                >
                  {s ? s.replace('_', ' ') : 'All'}
                </button>
              ))}
            </div>

            <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }} data-testid="consultant-applications-table">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                    <th style={th}>Candidate</th>
                    <th style={th}>Qualification</th>
                    <th style={th}>City / Exp</th>
                    <th style={th}>Test</th>
                    <th style={th}>Status</th>
                    <th style={th}>Applied</th>
                    <th style={{ ...th, textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {consultantApps.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }}>No consultant applications in this state.</td></tr>
                  )}
                  {consultantApps.map((a) => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`ca-app-row-${a.id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: '#0F172A' }}>{a.full_name}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748B' }}>{a.email}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748B' }}>{a.phone}</div>
                        <div style={{ fontSize: '0.72rem', color: '#FF6B35', fontFamily: 'monospace' }}>Alias: {a.alias}</div>
                      </td>
                      <td style={td}>
                        <div>{a.qualification}</div>
                        {a.icai_membership_no && <div style={{ fontSize: '0.75rem', color: '#64748B' }}>ICAI: {a.icai_membership_no}</div>}
                        {a.specialisation && <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{a.specialisation}</div>}
                      </td>
                      <td style={td}>
                        <div>{a.city || '—'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{a.experience_years} yr</div>
                      </td>
                      <td style={td}>
                        {a.test_score !== null && a.test_score !== undefined ? (
                          <span style={{ background: a.test_score >= 3 ? '#ECFDF5' : '#FEF2F2', color: a.test_score >= 3 ? '#065F46' : '#991B1B', padding: '2px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700 }}>
                            {a.test_score}/5
                          </span>
                        ) : <span style={{ color: '#94A3B8', fontSize: '0.78rem' }}>—</span>}
                      </td>
                      <td style={td}><span className={`status-badge status-${a.status === 'verified' ? 'approved' : a.status === 'rejected' ? 'rejected' : 'pending'}`}>{a.status.replace('_', ' ')}</span></td>
                      <td style={td}><span style={{ fontSize: '0.78rem', color: '#64748B' }}>{new Date(a.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => setViewingApp(a)} className="outline-button" style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }} data-testid={`ca-view-${a.id}`}>Review</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Messages (Contact Form Submissions) */}
        {activeTab === 'messages' && (
          <div className="fade-in" data-testid="admin-messages-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.95rem', color: '#475569' }}>
                Total messages: <strong style={{ color: '#0F172A' }}>{contactTotal}</strong>
              </div>
            </div>
            <ContactMessagesTable messages={contactMessages} />
          </div>
        )}

        {/* Marketing / Lead-gen */}
        {activeTab === 'marketing' && <MarketingTab />}

        {/* Ad Bookings */}
        {activeTab === 'ads' && (
          <div className="fade-in" data-testid="admin-ads-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['', 'pending', 'approved', 'active', 'paused', 'expired', 'rejected'].map((s) => (
                  <button
                    key={s || 'all'}
                    onClick={() => setAdFilter(s)}
                    data-testid={`ads-filter-${s || 'all'}`}
                    style={{
                      padding: '0.4rem 0.85rem',
                      background: adFilter === s ? '#FF6B35' : 'white',
                      color: adFilter === s ? 'white' : '#475569',
                      border: '1px solid ' + (adFilter === s ? '#FF6B35' : '#E2E8F0'),
                      borderRadius: 999, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize',
                    }}
                  >
                    {(s || 'all').replace('_', ' ')}{s && adsByStatus[s] !== undefined ? ` (${adsByStatus[s]})` : ''}
                  </button>
                ))}
              </div>
              <div style={{ background: '#FFF7ED', color: '#9A3412', padding: '6px 14px', borderRadius: 999, fontWeight: 700, fontSize: '0.85rem' }} data-testid="ads-revenue-pill">
                💰 Total bookings: ₹{Number(adsRevenue).toLocaleString('en-IN')}
              </div>
            </div>

            <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }} data-testid="ads-table">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                    <th style={th}>Advertiser</th>
                    <th style={th}>Slot / Headline</th>
                    <th style={th}>Duration</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                    <th style={{ ...th, textAlign: 'center' }}>Status</th>
                    <th style={{ ...th, textAlign: 'center' }}>Impr / Click</th>
                    <th style={{ ...th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map((a) => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`ad-row-${a.id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: '#0F172A' }}>{a.advertiser_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#475569' }}><a href={`mailto:${a.advertiser_email}`} style={{ color: '#FF6B35' }}>{a.advertiser_email}</a></div>
                        <div style={{ fontSize: '0.75rem', color: '#475569' }}><a href={`tel:${a.advertiser_phone}`} style={{ color: '#FF6B35' }}>{a.advertiser_phone}</a></div>
                        {a.company_website && <div style={{ fontSize: '0.7rem' }}><a href={a.company_website} target="_blank" rel="noreferrer" style={{ color: '#3B82F6' }}>{a.company_website.replace(/^https?:\/\//, '').slice(0, 30)}</a></div>}
                      </td>
                      <td style={td}>
                        <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>{a.slot_label || a.slot}</div>
                        <div style={{ fontWeight: 600, color: '#0F172A' }}>{a.headline}</div>
                        <div style={{ fontSize: '0.7rem', color: '#475569' }}>CTA: <a href={a.cta_url} target="_blank" rel="noreferrer" style={{ color: '#FF6B35' }}>{a.cta_text}</a></div>
                      </td>
                      <td style={{ ...td, color: '#475569' }}>{a.duration_months} mo</td>
                      <td style={{ ...td, textAlign: 'right', color: '#10B981', fontWeight: 700 }}>₹{Number(a.total_amount).toLocaleString('en-IN')}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <select
                          value={a.status}
                          onChange={(e) => handleUpdateAd(a.id, { status: e.target.value })}
                          data-testid={`ad-status-${a.id}`}
                          style={{ padding: '0.3rem 0.5rem', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: '0.8rem', background: 'white' }}
                        >
                          {['pending', 'approved', 'active', 'paused', 'expired', 'rejected'].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ ...td, textAlign: 'center', color: '#475569', fontSize: '0.825rem' }}>{a.impressions || 0} / {a.clicks || 0}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button onClick={() => handleDeleteAd(a.id)} className="outline-button" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', color: '#EF4444', borderColor: '#FECACA' }} data-testid={`ad-delete-${a.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {ads.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }} data-testid="no-ads">No ad bookings yet — share /advertise on your social channels</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments */}
        {activeTab === 'payments' && (
          <div className="fade-in" data-testid="admin-payments-section">
            <div style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', padding: '1.25rem 1.5rem', borderRadius: 14, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Total collected (Razorpay)</div>
                <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: 4 }} data-testid="payments-total-inr">₹{Number(paymentsTotalInr).toLocaleString('en-IN')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', opacity: 0.9 }}>
                <CreditCard size={18} /> Test mode · {payments.length} record{payments.length === 1 ? '' : 's'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {['', 'created', 'paid', 'failed', 'signature_failed'].map((s) => (
                <button
                  key={s || 'all'}
                  onClick={() => setPaymentFilter(s)}
                  data-testid={`payments-filter-${s || 'all'}`}
                  style={{
                    padding: '0.4rem 0.85rem',
                    background: paymentFilter === s ? '#FF6B35' : 'white',
                    color: paymentFilter === s ? 'white' : '#475569',
                    border: '1px solid ' + (paymentFilter === s ? '#FF6B35' : '#E2E8F0'),
                    borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                  }}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>

            <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }} data-testid="payments-table">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 900 }}>
                <thead style={{ background: '#F8FAFC' }}>
                  <tr>
                    <th style={th}>Created</th>
                    <th style={th}>Customer</th>
                    <th style={th}>For</th>
                    <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...th, textAlign: 'center' }}>Status</th>
                    <th style={th}>Razorpay IDs</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} style={{ borderTop: '1px solid #F1F5F9' }} data-testid={`payment-row-${p.id}`}>
                      <td style={{ ...td, fontSize: '0.78rem', color: '#475569' }}>{new Date(p.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: '#0F172A' }}>{p.customer_name || '—'}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748B' }}>{p.customer_email || ''}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748B' }}>{p.customer_phone || ''}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>{p.entity_type === 'tax_case' ? 'Tax Case' : 'Ad Booking'}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#0F172A' }}>{(p.entity_id || '').slice(0, 8)}</div>
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: '#0F172A', fontWeight: 700 }}>₹{Number((p.amount || 0) / 100).toLocaleString('en-IN')}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.6rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700,
                          background: p.status === 'paid' ? '#ECFDF5' : p.status === 'failed' || p.status === 'signature_failed' ? '#FEF2F2' : '#FEF3C7',
                          color: p.status === 'paid' ? '#065F46' : p.status === 'failed' || p.status === 'signature_failed' ? '#991B1B' : '#92400E',
                          border: '1px solid ' + (p.status === 'paid' ? '#A7F3D0' : p.status === 'failed' || p.status === 'signature_failed' ? '#FECACA' : '#FDE68A'),
                        }}>
                          {p.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.72rem', color: '#475569' }}>
                        <div><strong style={{ color: '#94A3B8' }}>order:</strong> {p.razorpay_order_id || '—'}</div>
                        <div><strong style={{ color: '#94A3B8' }}>pay:</strong> {p.razorpay_payment_id || '—'}</div>
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }} data-testid="no-payments">No payments yet. Once a customer pays a tax case or ad booking, you&apos;ll see entries here.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <p style={{ marginTop: '0.85rem', fontSize: '0.78rem', color: '#94A3B8' }}>
              You can also view all transactions in your <a href="https://dashboard.razorpay.com/app/payments" target="_blank" rel="noreferrer" style={{ color: '#FF6B35', fontWeight: 600 }}>Razorpay Dashboard →</a>
            </p>
          </div>
        )}

        {/* Users */}
        {activeTab === 'users' && (
          <div className="fade-in" data-testid="admin-users-section">
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <button
                className="outline-button"
                onClick={() => downloadFile(`${API}/admin/users/export`, 'users.csv')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}
                data-testid="export-users-csv"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
            <UsersTable users={users} />
          </div>
        )}

        {/* About Content */}
        {activeTab === 'content' && aboutContent && (
          <AboutContentEditor
            aboutContent={aboutContent}
            setAboutContent={setAboutContent}
            updateArrayField={updateArrayField}
            addArrayItem={addArrayItem}
            removeArrayItem={removeArrayItem}
            savingAbout={savingAbout}
            handleSaveAbout={handleSaveAbout}
          />
        )}

        {/* Update Application Modal */}
        {editingApp && (
          <Modal onClose={() => setEditingApp(null)} title="Update Application" subtitle={`${editingApp.service_name} - ${editingApp.bank_name}`} testId="update-app-modal">
            <div style={{ marginBottom: '1.25rem' }}>
              <label className="label-text">Status</label>
              <select className="input-field" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} data-testid="update-status-select">
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label-text">Admin Notes (Optional)</label>
              <textarea className="input-field" rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Visible to user in email" data-testid="update-notes-input" />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setEditingApp(null)} className="outline-button" style={{ flex: 1 }} data-testid="cancel-update-button">Cancel</button>
              <button onClick={handleUpdateApplication} className="primary-button" style={{ flex: 1, justifyContent: 'center' }} data-testid="confirm-update-button">Update & Notify</button>
            </div>
          </Modal>
        )}

        {/* Update Enquiry Modal */}
        {editingEnq && (
          <Modal onClose={() => setEditingEnq(null)} title="Update Enquiry" subtitle={`${editingEnq.name} - ${editingEnq.service_interested}`} testId="update-enq-modal">
            <div style={{ marginBottom: '1.25rem' }}>
              <label className="label-text">Status</label>
              <select className="input-field" value={enqForm.status} onChange={(e) => setEnqForm({ ...enqForm, status: e.target.value })} data-testid="update-enq-status">
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label-text">Internal Notes</label>
              <textarea className="input-field" rows={3} value={enqForm.admin_notes} onChange={(e) => setEnqForm({ ...enqForm, admin_notes: e.target.value })} placeholder="Internal only — not sent to enquirer" data-testid="update-enq-notes" />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setEditingEnq(null)} className="outline-button" style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleUpdateEnquiry} className="primary-button" style={{ flex: 1, justifyContent: 'center' }} data-testid="confirm-enq-update">Save</button>
            </div>
          </Modal>
        )}

        {/* Update Tax Case Modal */}
        {editingTax && (
          <Modal onClose={() => setEditingTax(null)} title="Update Tax Case" subtitle={`${editingTax.name} - ${editingTax.service_type}`} testId="update-tax-modal">
            <div style={{ marginBottom: '1rem' }}>
              <label className="label-text">Status</label>
              <select className="input-field" value={taxForm.status} onChange={(e) => setTaxForm({ ...taxForm, status: e.target.value })} data-testid="update-tax-status">
                <option value="new">New</option>
                <option value="documents_pending">Documents Pending</option>
                <option value="under_review">Under Review</option>
                <option value="filed">Filed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label className="label-text">Quoted Amount (₹)</label>
              <input className="input-field" type="number" value={taxForm.quoted_amount} onChange={(e) => setTaxForm({ ...taxForm, quoted_amount: e.target.value })} placeholder="e.g., 1499" data-testid="update-tax-amount" />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label className="label-text">Assign to Tax Consultant</label>
              <select className="input-field" value={taxForm.assigned_to} onChange={(e) => setTaxForm({ ...taxForm, assigned_to: e.target.value })} data-testid="update-tax-assign">
                <option value="">— Unassigned —</option>
                {consultants.map((c) => <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label-text">Expert Notes (visible to customer on dashboard)</label>
              <textarea className="input-field" rows={3} value={taxForm.admin_notes} onChange={(e) => setTaxForm({ ...taxForm, admin_notes: e.target.value })} placeholder="e.g., Documents look good. ITR will be filed by tomorrow." data-testid="update-tax-notes" />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setEditingTax(null)} className="outline-button" style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleUpdateTaxCase} className="primary-button" style={{ flex: 1, justifyContent: 'center', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }} data-testid="confirm-tax-update">Save</button>
            </div>
          </Modal>
        )}

        {viewingApp && (
          <Modal onClose={() => setViewingApp(null)} title={`Review CA: ${viewingApp.full_name}`} subtitle={`${viewingApp.qualification} · ${viewingApp.email}`} testId="ca-review-modal">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#475569' }}>
              <div><strong>Phone:</strong> {viewingApp.phone}</div>
              <div><strong>City:</strong> {viewingApp.city || '—'}</div>
              <div><strong>Experience:</strong> {viewingApp.experience_years} yrs</div>
              <div><strong>ICAI:</strong> {viewingApp.icai_membership_no || '—'}</div>
              <div><strong>Test:</strong> {viewingApp.test_score !== null ? `${viewingApp.test_score}/5` : '—'} ({viewingApp.test_attempts || 0} attempt{(viewingApp.test_attempts || 0) === 1 ? '' : 's'})</div>
              <div><strong>Alias:</strong> <span style={{ color: '#FF6B35', fontFamily: 'monospace' }}>{viewingApp.alias}</span></div>
              <div style={{ gridColumn: '1 / -1' }}><strong>Specialisation:</strong> {viewingApp.specialisation || '—'}</div>
            </div>

            {viewingApp.kyc ? (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>KYC Documents</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
                  {[
                    { k: 'pan_url', l: 'PAN' },
                    { k: 'aadhaar_url', l: 'Aadhaar' },
                    { k: 'ca_cert_url', l: 'CA Cert' },
                    { k: 'cheque_url', l: 'Cheque' },
                    { k: 'selfie_url', l: 'Selfie+PAN' },
                  ].map((d) => (
                    <a key={d.k} href={viewingApp.kyc?.[d.k]} target="_blank" rel="noreferrer" style={{ padding: '6px 10px', background: '#FFF7ED', color: '#9A3412', border: '1px solid #FED7AA', borderRadius: 8, fontSize: '0.78rem', textAlign: 'center', textDecoration: 'none', fontWeight: 700 }} data-testid={`ca-doc-${d.k}`}>
                      📎 {d.l}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: '0.75rem', background: '#FEF2F2', color: '#991B1B', borderRadius: 8, fontSize: '0.82rem', marginBottom: '1rem' }}>KYC not yet submitted.</div>
            )}

            {viewingApp.status === 'rejected' && viewingApp.rejection_reason && (
              <div style={{ padding: '0.75rem', background: '#FEF2F2', color: '#991B1B', borderRadius: 8, fontSize: '0.82rem', marginBottom: '1rem' }}>
                <strong>Rejection reason:</strong> {viewingApp.rejection_reason}
              </div>
            )}

            {viewingApp.status === 'kyc_submitted' && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => handleRejectConsultantApp(viewingApp.id)} className="outline-button" style={{ flex: 1, color: '#991B1B', borderColor: '#FCA5A5' }} data-testid="ca-reject-btn">
                  Reject
                </button>
                <button onClick={() => handleApproveConsultantApp(viewingApp.id)} className="primary-button" style={{ flex: 2, justifyContent: 'center', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }} data-testid="ca-approve-btn">
                  Approve & Send Login
                </button>
              </div>
            )}
            {viewingApp.status === 'verified' && (
              <div style={{ padding: '0.75rem', background: '#ECFDF5', color: '#065F46', borderRadius: 8, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={16} /> Verified consultant. Login credentials were emailed.
              </div>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = '#FF6B35', hint, testId }) => (
  <div className="stat-card" data-testid={testId} style={{ borderLeft: `4px solid ${color}` }}>
    {Icon && <Icon size={20} style={{ color, marginBottom: '0.4rem' }} />}
    <div style={{ fontSize: '0.8rem', color: '#475569' }}>{label}</div>
    <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', fontWeight: 700, color: '#0F172A' }}>{value}</div>
    {hint && <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: 2 }}>{hint}</div>}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="calculator-section" style={{ padding: '1.25rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
      <BarChart3 size={18} style={{ color: '#FF6B35' }} />
      <h3 style={{ fontSize: '1.05rem', color: '#0F172A', margin: 0 }}>{title}</h3>
    </div>
    {children}
  </div>
);

const EmptyMsg = ({ children }) => (
  <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: '#94A3B8', fontSize: '0.9rem' }}>{children}</div>
);

const Modal = ({ onClose, title, subtitle, testId, children }) => (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }} onClick={onClose}>
    <div style={{ backgroundColor: 'white', borderRadius: '1.25rem', padding: '1.75rem', maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()} data-testid={testId}>
      <h3 style={{ fontSize: '1.35rem', marginBottom: '0.25rem', color: '#0F172A' }}>{title}</h3>
      {subtitle && <p style={{ color: '#475569', marginBottom: '1.5rem', fontSize: '0.85rem' }}>{subtitle}</p>}
      {children}
    </div>
  </div>
);

const ApplicationsTable = ({ applications, onEdit }) => (
  <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }}>
    {/* Desktop table */}
    <table className="admin-desktop-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC' }}>
          <th style={th}>User</th>
          <th style={th}>Service</th>
          <th style={th}>Bank</th>
          <th style={{ ...th, textAlign: 'right' }}>Amount</th>
          <th style={{ ...th, textAlign: 'center' }}>Status</th>
          <th style={{ ...th, textAlign: 'center' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {applications.map((app) => (
          <tr key={app.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`admin-app-row-${app.id}`}>
            <td style={td}>
              <div style={{ fontWeight: 600, color: '#0F172A' }}>{app.user_name || 'N/A'}</div>
              <div style={{ fontSize: '0.7rem', color: '#475569' }}>{app.user_email}</div>
            </td>
            <td style={td}>{app.service_name}</td>
            <td style={td}>{app.bank_name}</td>
            <td style={{ ...td, textAlign: 'right', color: '#FF6B35', fontWeight: 700 }}>{app.amount ? `₹${app.amount.toLocaleString()}` : '-'}</td>
            <td style={{ ...td, textAlign: 'center' }}>
              <span className={`status-badge status-${app.status}`}>{app.status.replace('_', ' ')}</span>
            </td>
            <td style={{ ...td, textAlign: 'center' }}>
              <button onClick={() => onEdit(app)} className="outline-button" style={{ fontSize: '0.75rem', padding: '0.35rem 0.7rem' }} data-testid={`edit-app-${app.id}`}>Update</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {/* Mobile cards */}
    <div className="admin-mobile-cards" style={{ display: 'none', padding: '0.75rem' }}>
      {applications.map((app) => (
        <div key={app.id} style={{ padding: '0.9rem', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '0.75rem', marginBottom: '0.75rem' }} data-testid={`admin-app-card-${app.id}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{app.user_name || 'N/A'}</div>
            <span className={`status-badge status-${app.status}`}>{app.status.replace('_', ' ')}</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: 4 }}>{app.user_email}</div>
          <div style={{ fontSize: '0.85rem', color: '#0F172A' }}>{app.service_name} - {app.bank_name}</div>
          <div style={{ color: '#FF6B35', fontWeight: 700, marginTop: 6 }}>{app.amount ? `₹${app.amount.toLocaleString()}` : '-'}</div>
          <button onClick={() => onEdit(app)} className="outline-button" style={{ marginTop: 8, width: '100%', fontSize: '0.8rem' }} data-testid={`edit-app-mobile-${app.id}`}>Update</button>
        </div>
      ))}
    </div>
    {applications.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: '2rem' }}>No applications found</p>}
    <style>{`
      @media (max-width: 768px) {
        .admin-desktop-table { display: none; }
        .admin-mobile-cards { display: block !important; }
      }
    `}</style>
  </div>
);

const EnquiriesTable = ({ enquiries, onEdit, onDelete }) => (
  <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }}>
    <table className="admin-desktop-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC' }}>
          <th style={th}>Contact</th>
          <th style={th}>Service</th>
          <th style={th}>City</th>
          <th style={{ ...th, textAlign: 'right' }}>Amount</th>
          <th style={{ ...th, textAlign: 'center' }}>Status</th>
          <th style={th}>Created</th>
          <th style={{ ...th, textAlign: 'center' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {enquiries.map((en) => (
          <tr key={en.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`enquiry-row-${en.id}`}>
            <td style={td}>
              <div style={{ fontWeight: 600, color: '#0F172A' }}>{en.name}</div>
              <div style={{ fontSize: '0.7rem', color: '#475569' }}>{en.email}</div>
              <div style={{ fontSize: '0.7rem', color: '#475569' }}>{en.phone}</div>
            </td>
            <td style={td}>
              <div>{en.service_interested}</div>
              {en.bank_preference && <div style={{ fontSize: '0.7rem', color: '#64748B' }}>Pref: {en.bank_preference}</div>}
            </td>
            <td style={td}>{en.city || '-'}</td>
            <td style={{ ...td, textAlign: 'right', color: '#FF6B35', fontWeight: 700 }}>{en.loan_amount ? `₹${en.loan_amount.toLocaleString()}` : '-'}</td>
            <td style={{ ...td, textAlign: 'center' }}>
              <span style={{ background: `${STATUS_COLORS[en.status] || '#64748B'}22`, color: STATUS_COLORS[en.status] || '#64748B', padding: '0.25rem 0.6rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, textTransform: 'capitalize' }}>{en.status}</span>
            </td>
            <td style={{ ...td, fontSize: '0.75rem', color: '#64748B' }}>{new Date(en.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
            <td style={{ ...td, textAlign: 'center' }}>
              <button onClick={() => onEdit(en)} className="outline-button" style={{ fontSize: '0.7rem', padding: '0.3rem 0.6rem', marginRight: 4 }} data-testid={`edit-enq-${en.id}`}>Edit</button>
              <button onClick={() => onDelete(en.id)} style={{ background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: '0.4rem', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }} data-testid={`delete-enq-${en.id}`}>
                <Trash2 size={12} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="admin-mobile-cards" style={{ display: 'none', padding: '0.75rem' }}>
      {enquiries.map((en) => (
        <div key={en.id} style={{ padding: '0.9rem', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '0.75rem', marginBottom: '0.75rem' }} data-testid={`enquiry-card-${en.id}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{en.name}</div>
            <span style={{ background: `${STATUS_COLORS[en.status] || '#64748B'}22`, color: STATUS_COLORS[en.status] || '#64748B', padding: '0.2rem 0.5rem', borderRadius: 999, fontSize: '0.65rem', fontWeight: 700, textTransform: 'capitalize' }}>{en.status}</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#0F172A', marginBottom: 4 }}>{en.service_interested}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, fontSize: '0.75rem', color: '#64748B' }}>
            <a href={`tel:${en.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#FF6B35', textDecoration: 'none' }}><Phone size={12} /> {en.phone}</a>
            <a href={`mailto:${en.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#3B82F6', textDecoration: 'none' }}><Mail size={12} /> Email</a>
          </div>
          {en.loan_amount && <div style={{ color: '#FF6B35', fontWeight: 700, marginTop: 6 }}>₹{en.loan_amount.toLocaleString()}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={() => onEdit(en)} className="outline-button" style={{ flex: 1, fontSize: '0.8rem' }}>Edit</button>
            <button onClick={() => onDelete(en.id)} style={{ background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: '0.4rem', padding: '0.4rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Delete</button>
          </div>
        </div>
      ))}
    </div>
    {enquiries.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: '2rem' }}>No enquiries yet</p>}
  </div>
);

const ContactMessagesTable = ({ messages }) => (
  <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }} data-testid="contact-messages-table">
    <table className="admin-desktop-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC' }}>
          <th style={th}>From</th>
          <th style={th}>Subject</th>
          <th style={th}>Message</th>
          <th style={th}>Received</th>
        </tr>
      </thead>
      <tbody>
        {messages.map((m) => (
          <tr key={m.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`contact-message-row-${m.id}`}>
            <td style={td}>
              <div style={{ fontWeight: 600, color: '#0F172A' }}>{m.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} /> <a href={`mailto:${m.email}`} style={{ color: '#FF6B35' }}>{m.email}</a></div>
              {m.phone && <div style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}><Phone size={11} /> <a href={`tel:${m.phone}`} style={{ color: '#FF6B35' }}>{m.phone}</a></div>}
            </td>
            <td style={{ ...td, fontWeight: 600, color: '#0F172A' }}>{m.subject}</td>
            <td style={{ ...td, color: '#475569', maxWidth: 360, whiteSpace: 'pre-wrap' }}>{m.message}</td>
            <td style={{ ...td, color: '#475569', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {new Date(m.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {/* Mobile cards */}
    <div className="admin-mobile-cards" style={{ display: 'none', padding: '0.75rem' }}>
      {messages.map((m) => (
        <div key={m.id} style={{ padding: '0.9rem', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '0.75rem', marginBottom: '0.75rem' }} data-testid={`contact-message-card-${m.id}`}>
          <div style={{ fontWeight: 700, color: '#0F172A' }}>{m.name}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: 4 }}>{m.email}{m.phone ? ` • ${m.phone}` : ''}</div>
          <div style={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 600 }}>{m.subject}</div>
          <div style={{ marginTop: 6, color: '#475569', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{m.message}</div>
          <div style={{ marginTop: 8, fontSize: '0.7rem', color: '#94A3B8' }}>{new Date(m.created_at).toLocaleString('en-IN')}</div>
        </div>
      ))}
    </div>
    {messages.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: '2rem' }} data-testid="no-contact-messages">No contact messages yet</p>}
    <style>{`
      @media (max-width: 768px) {
        [data-testid="contact-messages-table"] .admin-desktop-table { display: none; }
        [data-testid="contact-messages-table"] .admin-mobile-cards { display: block !important; }
      }
    `}</style>
  </div>
);


const TaxCasesTable = ({ cases, consultants = [], onEdit, onDelete }) => {
  const consultantMap = Object.fromEntries((consultants || []).map((c) => [c.id, c.full_name]));
  return (
  <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }} data-testid="tax-cases-table">
    <table className="admin-desktop-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC' }}>
          <th style={th}>Contact</th>
          <th style={th}>Service</th>
          <th style={th}>PAN / City</th>
          <th style={{ ...th, textAlign: 'right' }}>Quoted</th>
          <th style={{ ...th, textAlign: 'center' }}>Status</th>
          <th style={th}>Created</th>
          <th style={{ ...th, textAlign: 'center' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {cases.map((c) => (
          <tr key={c.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`tax-case-row-${c.id}`}>
            <td style={td}>
              <div style={{ fontWeight: 600, color: '#0F172A' }}>{c.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#475569' }}><a href={`mailto:${c.email}`} style={{ color: '#FF6B35' }}>{c.email}</a></div>
              <div style={{ fontSize: '0.75rem', color: '#475569' }}><a href={`tel:${c.phone}`} style={{ color: '#FF6B35' }}>{c.phone}</a></div>
            </td>
            <td style={td}>
              <div style={{ fontWeight: 600, color: '#0F172A' }}>{c.service_type}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748B' }}>via {c.lead_source || c.utm_source || 'direct'}</div>
              {c.assigned_to && consultantMap[c.assigned_to] && (
                <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#065F46', background: '#ECFDF5', display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>👤 {consultantMap[c.assigned_to]}</div>
              )}
            </td>
            <td style={td}>
              <div style={{ fontFamily: 'monospace', color: '#10B981', fontWeight: 600 }}>{c.pan || '—'}</div>
              <div style={{ fontSize: '0.75rem', color: '#475569' }}>{c.city || ''}</div>
            </td>
            <td style={{ ...td, textAlign: 'right', color: '#10B981', fontWeight: 700 }}>{c.quoted_amount ? `₹${Number(c.quoted_amount).toLocaleString('en-IN')}` : '—'}</td>
            <td style={{ ...td, textAlign: 'center' }}>
              <span className={`status-badge status-${c.status === 'completed' || c.status === 'filed' ? 'approved' : c.status === 'cancelled' ? 'rejected' : 'pending'}`}>{c.status.replace('_', ' ')}</span>
            </td>
            <td style={{ ...td, color: '#475569', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </td>
            <td style={{ ...td, textAlign: 'center' }}>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                <button onClick={() => onEdit(c)} className="outline-button" style={{ fontSize: '0.75rem', padding: '0.35rem 0.7rem' }} data-testid={`edit-tax-${c.id}`}>Update</button>
                <button onClick={() => onDelete(c.id)} className="outline-button" style={{ fontSize: '0.75rem', padding: '0.35rem 0.7rem', color: '#EF4444', borderColor: '#FECACA' }} data-testid={`delete-tax-${c.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {/* Mobile cards */}
    <div className="admin-mobile-cards" style={{ display: 'none', padding: '0.75rem' }}>
      {cases.map((c) => (
        <div key={c.id} style={{ padding: '0.9rem', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '0.75rem', marginBottom: '0.75rem' }} data-testid={`tax-case-mobile-${c.id}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 700, color: '#0F172A' }}>{c.name}</div>
            <span className={`status-badge status-${c.status === 'completed' || c.status === 'filed' ? 'approved' : c.status === 'cancelled' ? 'rejected' : 'pending'}`}>{c.status.replace('_', ' ')}</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 600 }}>{c.service_type}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: 4 }}>{c.email} • {c.phone}</div>
          {c.quoted_amount ? <div style={{ color: '#10B981', fontWeight: 700, marginTop: 6 }}>₹{Number(c.quoted_amount).toLocaleString('en-IN')}</div> : null}
          <button onClick={() => onEdit(c)} className="outline-button" style={{ marginTop: 8, width: '100%', fontSize: '0.8rem' }} data-testid={`edit-tax-mobile-${c.id}`}>Update</button>
        </div>
      ))}
    </div>
    {cases.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: '2rem' }} data-testid="no-tax-cases-admin">No tax cases yet</p>}
    <style>{`
      @media (max-width: 768px) {
        [data-testid="tax-cases-table"] .admin-desktop-table { display: none; }
        [data-testid="tax-cases-table"] .admin-mobile-cards { display: block !important; }
      }
    `}</style>
  </div>
  );
};



const UsersTable = ({ users }) => (
  <div className="calculator-section" style={{ overflowX: 'auto', padding: 0 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC' }}>
          <th style={th}>Name</th>
          <th style={th}>Email</th>
          <th style={th}>Phone</th>
          <th style={th}>Referral</th>
          <th style={{ ...th, textAlign: 'center' }}>Apps</th>
          <th style={th}>Joined</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => (
          <tr key={u.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`admin-user-row-${u.id}`}>
            <td style={{ ...td, fontWeight: 600 }}>{u.full_name}</td>
            <td style={{ ...td, color: '#475569' }}>{u.email}</td>
            <td style={{ ...td, color: '#475569' }}>{u.phone}</td>
            <td style={{ ...td, fontFamily: 'monospace', color: '#FF6B35', fontWeight: 700 }}>{u.referral_code || '-'}</td>
            <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{u.application_count}</td>
            <td style={{ ...td, color: '#475569', fontSize: '0.8rem' }}>
              {new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {users.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: '2rem' }}>No users yet</p>}
  </div>
);

const AboutContentEditor = ({ aboutContent, setAboutContent, updateArrayField, addArrayItem, removeArrayItem, savingAbout, handleSaveAbout }) => (
  <div className="fade-in" data-testid="admin-content-section">
    <div className="calculator-section" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '1.15rem', marginBottom: '1.25rem', color: '#0F172A' }}>Mission & Vision</h3>
      <div style={{ marginBottom: '1rem' }}>
        <label className="label-text">Mission</label>
        <textarea className="input-field" rows={3} value={aboutContent.mission || ''} onChange={(e) => setAboutContent({ ...aboutContent, mission: e.target.value })} data-testid="admin-mission-textarea" />
      </div>
      <div>
        <label className="label-text">Vision</label>
        <textarea className="input-field" rows={3} value={aboutContent.vision || ''} onChange={(e) => setAboutContent({ ...aboutContent, vision: e.target.value })} data-testid="admin-vision-textarea" />
      </div>
    </div>

    <Section title="Team Members" onAdd={() => addArrayItem('team_members', { name: '', role: '', bio: '', image_url: '', linkedin: '' })} addTestId="add-team-member-button">
      {(aboutContent.team_members || []).map((m, idx) => (
        <div key={idx} style={cardBox} data-testid={`team-edit-${idx}`}>
          <div style={gridBox}>
            <input className="input-field" placeholder="Name" value={m.name || ''} onChange={(e) => updateArrayField('team_members', idx, 'name', e.target.value)} />
            <input className="input-field" placeholder="Role" value={m.role || ''} onChange={(e) => updateArrayField('team_members', idx, 'role', e.target.value)} />
            <input className="input-field" placeholder="LinkedIn URL" value={m.linkedin || ''} onChange={(e) => updateArrayField('team_members', idx, 'linkedin', e.target.value)} />
          </div>
          <textarea className="input-field" rows={2} placeholder="Bio" value={m.bio || ''} onChange={(e) => updateArrayField('team_members', idx, 'bio', e.target.value)} style={{ marginBottom: '0.5rem' }} />
          <RemoveBtn onClick={() => removeArrayItem('team_members', idx)} />
        </div>
      ))}
    </Section>

    <Section title="Awards & Recognition" onAdd={() => addArrayItem('awards', { title: '', year: '', issuer: '', description: '' })} addTestId="add-award-button">
      {(aboutContent.awards || []).map((a, idx) => (
        <div key={idx} style={cardBox} data-testid={`award-edit-${idx}`}>
          <div style={gridBox}>
            <input className="input-field" placeholder="Title" value={a.title || ''} onChange={(e) => updateArrayField('awards', idx, 'title', e.target.value)} />
            <input className="input-field" placeholder="Year" value={a.year || ''} onChange={(e) => updateArrayField('awards', idx, 'year', e.target.value)} />
            <input className="input-field" placeholder="Issuer" value={a.issuer || ''} onChange={(e) => updateArrayField('awards', idx, 'issuer', e.target.value)} />
          </div>
          <textarea className="input-field" rows={2} placeholder="Description" value={a.description || ''} onChange={(e) => updateArrayField('awards', idx, 'description', e.target.value)} style={{ marginBottom: '0.5rem' }} />
          <RemoveBtn onClick={() => removeArrayItem('awards', idx)} />
        </div>
      ))}
    </Section>

    <Section title="Press Coverage" onAdd={() => addArrayItem('press_coverage', { publication: '', headline: '', date: '', url: '', excerpt: '' })} addTestId="add-press-button">
      {(aboutContent.press_coverage || []).map((p, idx) => (
        <div key={idx} style={cardBox} data-testid={`press-edit-${idx}`}>
          <div style={gridBox}>
            <input className="input-field" placeholder="Publication" value={p.publication || ''} onChange={(e) => updateArrayField('press_coverage', idx, 'publication', e.target.value)} />
            <input className="input-field" placeholder="Date" value={p.date || ''} onChange={(e) => updateArrayField('press_coverage', idx, 'date', e.target.value)} />
            <input className="input-field" placeholder="URL" value={p.url || ''} onChange={(e) => updateArrayField('press_coverage', idx, 'url', e.target.value)} />
          </div>
          <input className="input-field" placeholder="Headline" value={p.headline || ''} onChange={(e) => updateArrayField('press_coverage', idx, 'headline', e.target.value)} style={{ marginBottom: '0.5rem' }} />
          <textarea className="input-field" rows={2} placeholder="Excerpt" value={p.excerpt || ''} onChange={(e) => updateArrayField('press_coverage', idx, 'excerpt', e.target.value)} style={{ marginBottom: '0.5rem' }} />
          <RemoveBtn onClick={() => removeArrayItem('press_coverage', idx)} />
        </div>
      ))}
    </Section>

    <button onClick={handleSaveAbout} disabled={savingAbout} className="primary-button" style={{ width: '100%', justifyContent: 'center', padding: '0.9rem' }} data-testid="save-about-button">
      {savingAbout ? 'Saving...' : 'Save All Changes'}
    </button>
  </div>
);

const Section = ({ title, onAdd, addTestId, children }) => (
  <div className="calculator-section" style={{ marginBottom: '1.5rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 8 }}>
      <h3 style={{ fontSize: '1.15rem', color: '#0F172A', margin: 0 }}>{title}</h3>
      <button onClick={onAdd} className="outline-button" style={{ fontSize: '0.8rem' }} data-testid={addTestId}>
        <Plus size={14} /> Add
      </button>
    </div>
    {children}
  </div>
);

const RemoveBtn = ({ onClick }) => (
  <button onClick={onClick} style={{ background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: '0.4rem', padding: '0.4rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <Trash2 size={12} /> Remove
  </button>
);

const th = { padding: '0.85rem', textAlign: 'left', fontSize: '0.8rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '0.85rem', color: '#0F172A', fontSize: '0.9rem' };
const cardBox = { padding: '1rem', background: '#F8FAFC', borderRadius: '0.75rem', marginBottom: '0.75rem' };
const gridBox = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.6rem' };

export default AdminDashboardPage;
